import { type ReactNode, useState } from 'react';
import { cn } from '@/nucleo/utilitarios';
import { CodeBlock } from './CodeBlock';
import { ScreenOnlyCode } from './print/PrintMode';

interface ComponentShowcaseProps {
  /** Id do item em `catalogo.ts`: é a âncora que a busca rola até a amostra. */
  id: string;
  title: string;
  description?: string;
  code: string;
  children: ReactNode;
  className?: string;
  /** Tabela de props, linha a linha: [Prop, Tipo, Padrão, Descrição]. */
  propsTable?: Array<[string, string, string, string]>;
}

export function ComponentShowcase({
  id,
  title,
  description,
  code,
  children,
  className,
  propsTable,
}: ComponentShowcaseProps) {
  const [showCode, setShowCode] = useState(false);

  return (
    <div
      className={cn(
        'ds-showcase rounded-xl border border-border bg-card overflow-hidden',
        className,
      )}
      data-search-id={id}
    >
      <div className="px-5 py-4 border-b border-border/50">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>

      {/* Preview */}
      <div className="ds-showcase-preview p-5 bg-background/50">
        <div className="flex flex-wrap items-center gap-3">{children}</div>
      </div>

      {/* Toggle + Code — existe só na tela, nunca no documento impresso */}
      <ScreenOnlyCode>
        <div className="border-t border-border/50">
          <button
            type="button"
            aria-expanded={showCode}
            onClick={() => setShowCode(!showCode)}
            className="w-full px-5 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors text-left"
          >
            {showCode ? 'Ocultar código' : 'Ver código'}
          </button>
          {showCode && (
            <div className="px-5 pb-4">
              <CodeBlock code={code} />
            </div>
          )}
        </div>
      </ScreenOnlyCode>

      {/* Props table */}
      {propsTable && propsTable.length > 0 && (
        <div className="border-t border-border/50 px-5 py-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Props
          </p>
          <div
            className="overflow-x-auto rounded-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a tabela rola na horizontal em 375 px e o teclado precisa alcançar a rolagem (WCAG 2.1.1)
            tabIndex={0}
            role="region"
            aria-label={`Props de ${title}`}
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left py-1.5 pr-4 font-semibold text-muted-foreground">
                    Prop
                  </th>
                  <th className="text-left py-1.5 pr-4 font-semibold text-muted-foreground">
                    Tipo
                  </th>
                  <th className="text-left py-1.5 pr-4 font-semibold text-muted-foreground">
                    Padrão
                  </th>
                  <th className="text-left py-1.5 font-semibold text-muted-foreground">
                    Descrição
                  </th>
                </tr>
              </thead>
              <tbody>
                {propsTable.map(([prop, type, def, desc]) => (
                  <tr key={prop} className="border-b border-border/30 last:border-0">
                    <td className="py-1.5 pr-4 font-mono text-foreground">{prop}</td>
                    <td className="py-1.5 pr-4 font-mono text-muted-foreground">{type}</td>
                    <td className="py-1.5 pr-4 font-mono text-muted-foreground">{def}</td>
                    <td className="py-1.5 text-muted-foreground">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
